import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs, { Dayjs } from "dayjs";

import { socket } from "../socket";
import { useBranchId } from "./useBranch";
import { buildDatePayload } from "../dateUtils";

import { getNewBookingsRequest } from "../../redux/actions/newAction";
import { getLiveBookingsRequest } from "../../redux/actions/liveAction";
import { getCompleteBookingByDateRequest } from "../../redux/actions/completeAction";
import { getCancelByDateRequest } from "../../redux/actions/cancelActions";
import {
    getEnquiryByDateRequest,
    getAvailableCabsRequest,
} from "../../redux/actions/bookingActions";
import { getDriverCallsRequest } from "../../redux/actions/driverCallsAction";
import { getEnterpriseEnquiryRequest } from "../../redux/actions/enterprisesEnquiryAction";
import { getAttachmentEnquiryRequest } from "../../redux/actions/attachmentEnquiryAction";

export type BookingCountKey =
    | "new"
    | "live"
    | "completed"
    | "cancel"
    | "enquiry"
    | "driverCalls"
    | "enterprise"
    | "availableCabs"
    | "attachment";

export interface BookingCount {
    key: BookingCountKey;
    label: string;
    value: number;
    loading: boolean;
    error: string | null;
    rangeLabel: string;
    path: string;
}

export interface DriverCallSplit {
    incoming: number;
    outgoing: number;
}

export interface UseBookingCountsResult {
    counts: Record<BookingCountKey, BookingCount>;
    driverCallSplit: DriverCallSplit;
    completedAmount: number;
    totalTrips: number;
    anyLoading: boolean;
    lastUpdated: Dayjs | null;
    refresh: () => void;
}

const RANGE_LABEL = "Today";

const buildTodayRange = () => {
    const today = dayjs();
    return buildDatePayload(today, today.add(1, "day"));
};

const SOCKET_EVENTS = [
    "booking_created",
    "booking_assigned",
    "booking_cancelled",
    "booking_canceled",
    "trip_reassigned",
    "enquiry_created",
    "enquiry_updated",
    "enquiry_to_booking",
] as const;

const REFRESH_DEBOUNCE_MS = 1500;

const toNumber = (v: any) => Number(v) || 0;

const NO_RECORDS = /records\s*not\s*found|status code 404/i;

const realError = (e: any): string | null => {
    if (!e) return null;
    const msg = typeof e === "string" ? e : e?.message ?? String(e);
    return NO_RECORDS.test(msg) ? null : msg;
};

interface UseBookingCountsOptions {
    live?: boolean;

    skip?: BookingCountKey[];
}

export default function useBookingCounts(
    opts: UseBookingCountsOptions = {}
): UseBookingCountsResult {
    const { live = true, skip = [] } = opts;

    const dispatch = useDispatch();
    const [lastUpdated, setLastUpdated] = useState<Dayjs | null>(null);

    const branchId = useBranchId();

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const skipRef = useRef(skip);
    skipRef.current = skip;

    const newBooking = useSelector((s: any) => s.newBooking || {});
    const liveState = useSelector((s: any) => s.live || {});
    const complete = useSelector((s: any) => s.complete || {});
    const cancel = useSelector((s: any) => s.cancel || {});
    const booking = useSelector((s: any) => s.booking || {});
    const driverCalls = useSelector((s: any) => s.driverCalls || {});
    const enterprise = useSelector((s: any) => s.enterpriseEnquiry || {});
    const attachment = useSelector((s: any) => s.attachmentEnquiry || {});

    const loadNew = Boolean(newBooking.newBookingsLoading);
    const loadLive = Boolean(liveState.loading);
    const loadCompleted = Boolean(complete.completeBookingsLoading);
    const loadCancel = Boolean(cancel.cancelLoading);
    const loadEnquiry = Boolean(booking.enquiriesLoading);
    const loadDriverCalls = Boolean(driverCalls.loading);
    const loadEnterprise = Boolean(enterprise.loading);
    const loadAvailableCabs = Boolean(booking.loading);
    const loadAttachment = Boolean(attachment.getLoading);

    const [settled, setSettled] = useState<Record<BookingCountKey, boolean>>({
        new: false,
        live: false,
        completed: false,
        cancel: false,
        enquiry: false,
        driverCalls: false,
        enterprise: false,
        availableCabs: false,
        attachment: false,
    });

    const prevLoading = useRef<Record<BookingCountKey, boolean>>({
        new: false,
        live: false,
        completed: false,
        cancel: false,
        enquiry: false,
        driverCalls: false,
        enterprise: false,
        availableCabs: false,
        attachment: false,
    });

    useEffect(() => {
        const now: Record<BookingCountKey, boolean> = {
            new: loadNew,
            live: loadLive,
            completed: loadCompleted,
            cancel: loadCancel,
            enquiry: loadEnquiry,
            driverCalls: loadDriverCalls,
            enterprise: loadEnterprise,
            availableCabs: loadAvailableCabs,
            attachment: loadAttachment,
        };
        const prev = prevLoading.current;
        prevLoading.current = now;

        const keys = Object.keys(now) as BookingCountKey[];
        const done = keys.filter(
            (k) => (prev[k] && !now[k]) || skipRef.current.includes(k)
        );

        if (done.length) {
            setSettled((s) => {
                if (done.every((k) => s[k])) return s;
                const next = { ...s };
                done.forEach((k) => (next[k] = true));
                return next;
            });
        }
    }, [
        loadNew,
        loadLive,
        loadCompleted,
        loadCancel,
        loadEnquiry,
        loadDriverCalls,
        loadEnterprise,
        loadAvailableCabs,
        loadAttachment,
    ]);

    const fetchAll = useCallback(() => {
        const { fromDate, toDate } = buildTodayRange();
        const skipped = skipRef.current;
        const wanted = (k: BookingCountKey) => !skipped.includes(k);

        if (wanted("new")) dispatch(getNewBookingsRequest(branchId));
        if (wanted("live")) dispatch(getLiveBookingsRequest({ branchid: branchId }));
        if (wanted("completed")) {
            dispatch(getCompleteBookingByDateRequest(branchId, fromDate, toDate));
        }
        if (wanted("cancel")) {
            dispatch(getCancelByDateRequest(branchId, fromDate, toDate));
        }
        if (wanted("enquiry")) {
            dispatch(getEnquiryByDateRequest(branchId, fromDate, toDate));
        }
        if (wanted("driverCalls")) {
            dispatch(getDriverCallsRequest(fromDate, toDate));
        }
        if (wanted("enterprise")) {
            const today = dayjs().format("YYYY-MM-DD");
            dispatch(getEnterpriseEnquiryRequest({ fromdate: today, todate: today }));
        }
        if (wanted("availableCabs")) {
            dispatch(getAvailableCabsRequest(branchId, ""));
        }
        if (wanted("attachment")) {
            dispatch(
                getAttachmentEnquiryRequest({
                    cityid: "",
                    fromdate: fromDate,
                    todate: toDate,
                    type: "",
                    status: "",
                })
            );
        }

        setLastUpdated(dayjs());
    }, [dispatch, branchId]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const debouncedRefresh = useCallback(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(fetchAll, REFRESH_DEBOUNCE_MS);
    }, [fetchAll]);

    useEffect(() => {
        if (!live) return;

        const onEvent = () => {
            if (document.visibilityState === "hidden") return;
            debouncedRefresh();
        };

        const onVisible = () => {
            if (document.visibilityState === "visible") debouncedRefresh();
        };

        SOCKET_EVENTS.forEach((e) => socket.on(e, onEvent));
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            SOCKET_EVENTS.forEach((e) => socket.off(e, onEvent));
            document.removeEventListener("visibilitychange", onVisible);
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [live, debouncedRefresh]);

    const counts = useMemo<Record<BookingCountKey, BookingCount>>(() => {

        return {
            new: {
                key: "new",
                label: "New Bookings",
                value: toNumber(newBooking.todaycount),
                loading: !settled.new,
                error: realError(newBooking.newBookingsError),
                rangeLabel: RANGE_LABEL,
                path: "/booking/new",
            },
            live: {
                key: "live",
                label: "Live Trips",
                value: toNumber(liveState.tableLength),
                loading: !settled.live,
                error: realError(liveState.error),
                rangeLabel: RANGE_LABEL,
                path: "/booking/live",
            },
            completed: {
                key: "completed",
                label: "Completed",
                value: toNumber(complete.tablelength),
                loading: !settled.completed,
                error: realError(complete.completeBookingsError),
                rangeLabel: RANGE_LABEL,
                path: "/booking/completed",
            },
            cancel: {
                key: "cancel",
                label: "Cancelled",
                value: Array.isArray(cancel.cancelledBookings)
                    ? cancel.cancelledBookings.length
                    : 0,
                loading: !settled.cancel,
                error: realError(cancel.cancelError),
                rangeLabel: RANGE_LABEL,
                path: "/booking/cancel",
            },
            enquiry: {
                key: "enquiry",
                label: "Enquiries",
                value:
                    toNumber(booking.enquiriesCount) ||
                    (Array.isArray(booking.enquiries) ? booking.enquiries.length : 0),
                loading: !settled.enquiry,
                error: realError(booking.enquiriesError),
                rangeLabel: RANGE_LABEL,
                path: "/booking/enquiry",
            },
            driverCalls: {
                key: "driverCalls",
                label: "Driver Calls",
                value:
                    toNumber(driverCalls.total) ||
                    (Array.isArray(driverCalls.data) ? driverCalls.data.length : 0),
                loading: !settled.driverCalls,
                error: realError(driverCalls.error),
                rangeLabel: RANGE_LABEL,
                path: "/driverreport/driver-call",
            },
            enterprise: {
                key: "enterprise",
                label: "Enterprise Enquiries",
                value:
                    Array.isArray(enterprise.list) && enterprise.list.length
                        ? toNumber(enterprise.totalcount) || enterprise.list.length
                        : 0,
                loading: !settled.enterprise,
                error: realError(enterprise.error),
                rangeLabel: RANGE_LABEL,
                path: "/EnterpriseEnquiry",
            },
            availableCabs: {
                key: "availableCabs",
                label: "Available Cabs",
                value: Array.isArray(booking.availableCabs)
                    ? booking.availableCabs.length
                    : 0,
                loading: !settled.availableCabs,
                error: realError(booking.availableCabsError),
                rangeLabel: "On duty",
                path: "/AvailableCabs",
            },
            attachment: {
                key: "attachment",
                label: "Attachment Enquiries",
                value:
                    toNumber(attachment.tablelength) ||
                    (Array.isArray(attachment.list) ? attachment.list.length : 0),
                loading: !settled.attachment,
                error: realError(attachment.getError),
                rangeLabel: RANGE_LABEL,
                path: "/aenquiry/AEnquiry",
            },
        };
    }, [
        newBooking,
        liveState,
        complete,
        cancel,
        booking,
        driverCalls,
        enterprise,
        attachment,
        settled,
    ]);

    const driverCallSplit = useMemo<DriverCallSplit>(() => {
        const rows: any[] = Array.isArray(driverCalls.data) ? driverCalls.data : [];
        const countType = (t: string) =>
            rows.filter((r) => String(r?.type ?? "").toLowerCase() === t).length;

        return { incoming: countType("incoming"), outgoing: countType("outgoing") };
    }, [driverCalls.data]);

    const totalTrips =
        counts.new.value +
        counts.live.value +
        counts.completed.value +
        counts.cancel.value;

    const anyLoading = Boolean(
        newBooking.newBookingsLoading ||
        liveState.loading ||
        complete.completeBookingsLoading ||
        cancel.cancelLoading ||
        booking.enquiriesLoading ||
        driverCalls.loading ||
        enterprise.loading ||
        attachment.getLoading
    );

    return {
        counts,
        driverCallSplit,
        completedAmount: toNumber(complete.totalamount),
        totalTrips,
        anyLoading,
        lastUpdated,
        refresh: fetchAll,
    };
}
